import {
    Box,
    ClickAwayListener,
    Grid,
    Popper
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { ActionButton } from '@tassat/button';
import { ArrowDateRange } from '@tassat/icon';
import { ApplicationMuiTheme } from '@tassat/payments-themes';
import { clientMessageInitialDateRange } from '@tassat/ui-definitions';
import moment from 'moment';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import MonthSelector from './MonthSelector';
import muiStyles from './styles';
import {
    DEFAULT_MAX_DATE,
    DEFAULT_MIN_DATE,
    DateRangePickerProps,
    END_DATE_ID,
    START_DATE_ID
} from './types';

import 'react-day-picker/style.css';
import './dayPickerOverrides.css';

const convertDateFilterToTime = (timeFrame) => {
    switch (timeFrame) {
        case '1d':
            return moment()
                .subtract(1, 'day')
                .format('x');
        case '1w':
            return moment()
                .subtract(1, 'week')
                .format('x');
        case '1m':
            return moment()
                .subtract(1, 'month')
                .format('x');
        case '3m':
            return moment()
                .subtract(3, 'month')
                .format('x');
        case '6m':
            return moment()
                .subtract(6, 'month')
                .format('x');
        case '1y':
            return moment()
                .subtract(1, 'year')
                .format('x');
        case '18m':
            return moment()
                .subtract(18, 'month')
                .format('x');
        case 'YTD':
            return new Date(new Date()
                .getFullYear(), 0, 1)
                .valueOf();
        default:
            return '1d';
    }
};

export default function DateRangeSelector(props: DateRangePickerProps) {
    const {
        className,
        timeFrameInDays,
        field,
        selectedDateField,
        selectTimeRange,
        allowSingleDay,
        minDate = DEFAULT_MIN_DATE,
        maxDate = DEFAULT_MAX_DATE,
        disabled
    } = props;

    const muiTheme = useTheme<ApplicationMuiTheme>();
    const classes = muiStyles(props);
    const convertTimeFrameInDays = React.useCallback(() => {
        return moment.unix(
            Number(convertDateFilterToTime(timeFrameInDays)) / 1000
        );
    }, [timeFrameInDays]);

    const [ rangeDate, setRangeDate ] = React.useState<{
        startDate: moment.Moment;
        endDate: moment.Moment;
    }>({
        startDate: timeFrameInDays && timeFrameInDays !== 'clientMessageInitialState' ?
            convertTimeFrameInDays()
            : moment(),
        endDate: moment()
    });

    const [ focusedInput, setFocusedInput ] = React.useState<'startDate' | 'endDate' | null>(null);
    const startDateInputRef = React.useRef<HTMLInputElement>(null);
    const arrowRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (
            !timeFrameInDays ||
            (timeFrameInDays && timeFrameInDays === 'none') ||
            (timeFrameInDays && field !== selectedDateField)
        ) {
            setRangeDate({
                startDate: moment(),
                endDate: moment()
            });
        } else if (selectedDateField === field && timeFrameInDays === 'clientMessageInitialState') {
            setRangeDate({
                startDate: clientMessageInitialDateRange.StartAt.start,
                endDate: clientMessageInitialDateRange.StartAt.end
            });
        } else if (timeFrameInDays !== 'customRange') {
            setRangeDate({
                startDate: convertTimeFrameInDays(),
                endDate: moment()
            });
        }
    }, [ timeFrameInDays, selectedDateField, field, convertTimeFrameInDays ]);

    const applyDateRange = () => {
        if (rangeDate.endDate?.valueOf() > rangeDate.startDate.valueOf()) {
            selectTimeRange(rangeDate.startDate, rangeDate.endDate, field);
        }
    };

    const isNotSelectableDate = (date) => {
        const momentDate = moment(date);
        let isNotValid = false;
        if (maxDate && momentDate.valueOf() > maxDate.valueOf()) {
            isNotValid = true;
        }

        if (minDate && momentDate.valueOf() < minDate.valueOf()) {
            isNotValid = true;
        }

        if (focusedInput === END_DATE_ID && (
            (allowSingleDay && momentDate.isBefore(rangeDate.startDate)) ||
            (!allowSingleDay && momentDate.isSameOrBefore(rangeDate.startDate))
        )) {
            isNotValid = true;
        }

        return isNotValid;
    };

    const openToSelectedDate = () => {
        if (focusedInput === START_DATE_ID && rangeDate.startDate) {
            return rangeDate.startDate;
        }

        if (focusedInput === END_DATE_ID && rangeDate.endDate) {
            return rangeDate.endDate;
        }

        return moment();
    };

    return (
        <Grid
            alignItems='center'
            className={`${classes.root} ${className}`}
            container
            direction='row'
            wrap='nowrap'
        >
            <ClickAwayListener onClickAway={() => setFocusedInput(null)}>
                <Grid item>
                    <Grid
                        className={classes.dateRangeInput}
                        container
                        justifyContent='space-between'
                    >
                        <Grid item>
                            <input
                                aria-label='Start Date'
                                className={`${classes.dateInput} ${focusedInput === START_DATE_ID ? classes.dateInputFocused : ''}`}
                                onClick={() => {
                                    setFocusedInput(START_DATE_ID);
                                }}
                                readOnly
                                ref={startDateInputRef}
                                value={rangeDate.startDate.format('MMM DD, YYYY')}
                            />
                        </Grid>
                        <Grid
                            className={classes.dateRangeArrow}
                            item
                        >
                            <ArrowDateRange className={classes.arrowSvg} />
                        </Grid>
                        <Grid item>
                            <input
                                aria-label='End Date'
                                className={`${classes.dateInput} ${focusedInput === END_DATE_ID ? classes.dateInputFocused : ''}`}
                                onClick={() => {
                                    setFocusedInput(END_DATE_ID);
                                }}
                                readOnly
                                value={rangeDate.endDate.format('MMM DD, YYYY')}
                            />
                        </Grid>
                    </Grid>
                    <Popper
                        anchorEl={startDateInputRef.current}
                        className={classes.popper}
                        modifiers={[
                            {
                                enabled: true,
                                name: 'arrow',
                                options: { element: arrowRef.current }
                            },
                            {
                                name: 'flip',
                                enabled: false
                            },
                            {
                                name: 'offset',
                                options: { offset: [ 0, 12 ]}
                            }
                        ]}
                        open={focusedInput !== null}
                        placement='bottom-start'
                        popperOptions={{ strategy: 'fixed' }}
                        sx={{
                            '--color-primary-selected': muiTheme?.app?.clientTheme.colors.primaryColor,
                            '--color-primary-range': muiTheme?.app?.clientTheme.colors.buttons.primaryHover
                        }}
                    >
                        <Box
                            className={`${classes.popperArrow} ${focusedInput === END_DATE_ID ? classes.arrowEndDate : classes.arrowStartDate}`}
                            ref={arrowRef}
                        />
                        <DayPicker
                            components={{ MonthCaption: MonthSelector }}
                            defaultMonth={openToSelectedDate()
                                .toDate()}
                            disabled={isNotSelectableDate}
                            endMonth={new Date(maxDate.year(), maxDate.month())}
                            min={allowSingleDay ? 0 : 1}
                            mode='range'
                            navLayout='around'
                            numberOfMonths={2}
                            onDayClick={(value: Date) => {
                                let newStartDate;
                                let newEndDate;
                                if (focusedInput === START_DATE_ID) {
                                    newStartDate = moment(value);
                                    if (newStartDate && rangeDate.endDate && newStartDate.isAfter(rangeDate.endDate)) {
                                        newEndDate = allowSingleDay ? newStartDate : newStartDate.add('1', 'day');
                                    } else {
                                        newEndDate = rangeDate.endDate;
                                    }
                                    setFocusedInput(END_DATE_ID);
                                } else if (focusedInput === END_DATE_ID) {
                                    newStartDate = rangeDate.startDate;
                                    newEndDate = moment(value);
                                    setFocusedInput(null);
                                }
                                setRangeDate({
                                    startDate: newStartDate,
                                    endDate: newEndDate
                                });
                            }}
                            // The onSelect prop is necessary to be able to control the internal state of the Calendar with selected prop
                            onSelect={() => undefined}
                            selected={{
                                from: new Date(rangeDate.startDate.toDate()),
                                to: new Date(rangeDate.endDate.toDate())
                            }}
                            startMonth={new Date(minDate.year(), minDate.month())}
                        />
                    </Popper>
                </Grid>
            </ClickAwayListener>
            <Grid item>
                <ActionButton
                    className={classes.applyButton}
                    disabled={disabled}
                    handleClick={applyDateRange}
                    text='Apply'
                    type='primary'
                />
            </Grid>
        </Grid>
    );
}